import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, FileText, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function FileUploadZone({ files, setFiles, descriptions, setDescriptions, onUpload, uploading }) {
  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prev) => [...prev, ...acceptedFiles]);
    setDescriptions((prev) => ({
      ...prev,
      ...Object.fromEntries(acceptedFiles.map((f, i) => [prev ? Object.keys(prev).length + i : i, ""]))
    }));
  }, [setFiles, setDescriptions]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setDescriptions((prev) => {
      const updated = {};
      let newIdx = 0;
      Object.keys(prev).forEach((key) => {
        if (Number(key) !== index) {
          updated[newIdx] = prev[key];
          newIdx++;
        }
      });
      return updated;
    });
  };

  const updateDescription = (index, value) => {
    setDescriptions((prev) => ({ ...prev, [index]: value }));
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        data-testid="file-dropzone"
        className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragActive
            ? "border-[#003057] bg-[#003057]/5"
            : "border-slate-300 hover:border-[#003057]/50 hover:bg-slate-50"
        }`}
      >
        <input {...getInputProps()} data-testid="file-input" />
        <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" strokeWidth={1.5} />
        <p className="text-sm font-medium text-slate-700">
          {isDragActive ? "Drop files here" : "Drag & drop files or click to browse"}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          PDF, DOCX, XLSX, CSV, LOG, JSON, XML, CAN logs, Python, C/C++, Images
        </p>
      </div>

      {files.length > 0 && (
        <div className="space-y-3" data-testid="file-list">
          {files.map((file, i) => (
            <div
              key={i}
              className="bg-white border border-slate-200 rounded-sm px-3 py-3 space-y-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-[#003057] shrink-0" strokeWidth={1.5} />
                  <span className="text-sm font-medium text-slate-700 truncate">{file.name}</span>
                  <span className="text-xs text-slate-400">
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`remove-file-${i}`}
                  onClick={() => removeFile(i)}
                  className="text-slate-400 hover:text-red-500 h-6 w-6 p-0"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Input
                data-testid={`file-description-${i}`}
                value={descriptions[i] || ""}
                onChange={(e) => updateDescription(i, e.target.value)}
                placeholder="Describe this file — e.g., 'Previous HIL test report with CAN timing data from v3.1.0'"
                className="rounded-sm text-xs h-8 text-slate-600 placeholder:text-slate-400"
              />
            </div>
          ))}
          <Button
            data-testid="upload-files-btn"
            onClick={onUpload}
            disabled={uploading}
            className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm font-medium w-full"
          >
            {uploading ? "Uploading..." : `Upload ${files.length} file${files.length > 1 ? "s" : ""}`}
          </Button>
        </div>
      )}
    </div>
  );
}
